import { collection, deleteDoc, doc, getDoc, getDocs, serverTimestamp, setDoc } from 'firebase/firestore'
import { db } from '@/services/firebase/config'
import { callUser } from '@/services/firebase/callable'
import {
  buildCharacterCampaignPreview,
  resolveCharacterCampaignIds
} from '@/utils/characterCampaignPreview'

export const buildCampaignCharacterPreviewDoc = (characterId, character = {}) => ({
  characterId,
  owner: character?.owner || character?.ownerId || character?.userId || character?.createdBy || null,
  ...buildCharacterCampaignPreview(character),
  updatedAt: serverTimestamp()
})

export const getCampaignCharacterPreviewDocRef = (campaignId, characterId) =>
  doc(db, 'campaigns', String(campaignId || '').trim(), 'characterPreviews', String(characterId || '').trim())

const CampaignCharacterPreviewService = {
  async getCampaignCharacterPreviews(campaignId, characterIds = []) {
    const safeCampaignId = String(campaignId || '').trim()
    const safeCharacterIds = Array.from(
      new Set(
        (Array.isArray(characterIds) ? characterIds : []).map((id) => String(id || '').trim())
      )
    ).filter(Boolean)

    if (!safeCampaignId) return []

    if (!safeCharacterIds.length) {
      const snapshot = await getDocs(collection(db, 'campaigns', safeCampaignId, 'characterPreviews'))
      return snapshot.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data()
      }))
    }

    const previews = await Promise.allSettled(
      safeCharacterIds.map(async (characterId) => {
        const snapshot = await getDoc(getCampaignCharacterPreviewDocRef(safeCampaignId, characterId))
        if (!snapshot.exists()) return null
        return {
          id: snapshot.id,
          ...snapshot.data()
        }
      })
    )

    return previews
      .map((result) => (result.status === 'fulfilled' ? result.value : null))
      .filter(Boolean)
  },

  async upsertCampaignCharacterPreview(campaignId, characterId, character) {
    const safeCampaignId = String(campaignId || '').trim()
    const safeCharacterId = String(characterId || '').trim()
    if (!safeCampaignId || !safeCharacterId) return

    await setDoc(
      getCampaignCharacterPreviewDocRef(safeCampaignId, safeCharacterId),
      buildCampaignCharacterPreviewDoc(safeCharacterId, character),
      { merge: true }
    )
  },

  async deleteCampaignCharacterPreview(campaignId, characterId) {
    const safeCampaignId = String(campaignId || '').trim()
    const safeCharacterId = String(characterId || '').trim()
    if (!safeCampaignId || !safeCharacterId) return

    await deleteDoc(getCampaignCharacterPreviewDocRef(safeCampaignId, safeCharacterId))
  },

  async syncCharacterPreviews(characterId, character) {
    const safeCharacterId = String(characterId || character?.id || '').trim()
    if (!safeCharacterId || !character) return

    const campaignIds = resolveCharacterCampaignIds(character)
    if (!campaignIds.length) return

    await Promise.all(
      campaignIds.map((campaignId) =>
        this.upsertCampaignCharacterPreview(campaignId, safeCharacterId, character)
      )
    )
  },

  async deleteCharacterPreviews(characterId, campaignIds = []) {
    const safeCharacterId = String(characterId || '').trim()
    const safeCampaignIds = Array.from(
      new Set((Array.isArray(campaignIds) ? campaignIds : []).map((id) => String(id || '').trim()))
    ).filter(Boolean)

    if (!safeCharacterId || !safeCampaignIds.length) return

    await Promise.all(
      safeCampaignIds.map((campaignId) =>
        this.deleteCampaignCharacterPreview(campaignId, safeCharacterId)
      )
    )
  },

  async backfillCampaignCharacterPreviews(campaignId, characterIds = []) {
    const safeCampaignId = String(campaignId || '').trim()
    const safeCharacterIds = Array.from(
      new Set(
        (Array.isArray(characterIds) ? characterIds : []).map((id) => String(id || '').trim())
      )
    ).filter(Boolean)

    if (!safeCampaignId) return []

    const response = await callUser(
      'backfillCampaignCharacterPreviews',
      {
        campaignId: safeCampaignId,
        characterIds: safeCharacterIds
      },
      { timeout: 120000, retries: 0 }
    )

    return Array.isArray(response?.data?.previews) ? response.data.previews : []
  }
}

export default CampaignCharacterPreviewService
